import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Diagnoskod = {
    id: number;
    code: string;
    displayName: string;
    valid_from_date: string;
    valid_to_date: string;
    is_valid_now: boolean;
};

export type DiagnoskodDisplay = {
    value: Diagnoskod["id"];
    label: string;
};

type State = BaseState & {
    diagnoskoder: Map<Diagnoskod["id"], Diagnoskod>;
};

export const convertDiagnoskodData: (args: { diagnoskod?: Diagnoskod }) => DiagnoskodDisplay | null = ({
    diagnoskod,
}) => {
    if (!diagnoskod) {
        return null;
    }

    return {
        value: diagnoskod.id,
        label: `${diagnoskod.code} ${diagnoskod.displayName}`,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        diagnoskoder: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "diagnoskoder";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        diagnoskoder: (state: State) => [...state.diagnoskoder.values()],
        diagnoskodSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertDiagnoskodData({ diagnoskod: singleResult }));
            }

            return result;
        },
        diagnoskodById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.diagnoskoder.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addDiagnoskoder: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                diagnoskoder = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                diagnoskoder: Diagnoskod[];
            }
        ) =>
            subsetSetter<State, Diagnoskod>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: diagnoskoder,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchDiagnoskoder: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Diagnoskod>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "core/kodverk/icdkoder/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addDiagnoskoder", {
                    subsetName,
                    offset: resultOffset,
                    diagnoskoder: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertDiagnoskodData({ diagnoskod: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
